/**
 * KB view — detail view rendering.
 */

import { esc, relTime, renderMd } from '../../shared/rendering.js'
import { action } from '../components/action-delegation.js'
import { fetchKBEntryDetail, fetchKBReadStats } from '../../api/kb.js'
import type { KBEntryDetail } from '../../types/api.js'
import type { KBEntryReadStats } from '../../api/kb.js'
import { kbTypeColor, workStatusColor } from './kb_types.js'

export async function renderKBDetailView(entryId: string): Promise<void> {
  const app = document.getElementById('app')
  if (!app) {
    return
  }

  document.title = `${entryId} \u2014 Grizzly`
  app.innerHTML = '<div class="loading"><div class="spinner"></div></div>'

  try {
    const [entry, reads] = await Promise.all([
      fetchKBEntryDetail(entryId),
      fetchKBReadStats(entryId).catch(() => null),
    ])
    app.innerHTML = renderKBDetail(entry, reads)
    // preventScroll: see fleet.ts focus() call — keeps sticky topbar pinned.
    app.focus({ preventScroll: true })
  } catch (err) {
    app.innerHTML = `<div class="rh"><a class="back" ${action('navigate', '/kb')}>&larr;</a><div class="rh-info"><h1 class="rh-t">${esc(entryId)}</h1><span class="rh-sub">Failed to load entry. ${esc(String(err))}</span></div></div>`
  }
}

function renderLinkedWork(linkedWork: KBEntryDetail['linked_work_items']): string {
  if (!linkedWork || linkedWork.length === 0) {
    return ''
  }
  const rows = linkedWork
    .map((w) => {
      const wsc = workStatusColor(w.status || 'open')
      return `<div style="display:flex;align-items:center;gap:8px;padding:5px 10px;background:var(--s1);border:1px solid var(--bd);border-radius:6px;cursor:pointer" tabindex="0" role="link" ${action('navigate', `/work/${w.id}`)}><span style="font-family:var(--mono);font-size:10px;color:var(--t3)">${esc(w.id)}</span><span style="font-size:12px;flex:1">${esc(w.title || '')}</span><span style="font-family:var(--mono);font-size:9px;padding:1px 6px;border-radius:3px;background:var(--${wsc}-d);color:var(--${wsc})">${esc((w.status || '').replace(/_/g, ' '))}</span></div>`
    })
    .join('')
  return `<div class="sl" style="margin-top:16px">Linked Work Items</div><div style="display:flex;flex-direction:column;gap:4px;margin-bottom:14px">${rows}</div>`
}

function renderRelatedKB(related: KBEntryDetail['related_kb']): string {
  if (!related || related.length === 0) {
    return ''
  }
  const rows = related
    .map((r) => {
      const rc = kbTypeColor(r.type || '')
      const rpreview = r.labels?.join(', ') || ''
      return `<div class="kb-related-item" style="padding:8px 12px;background:var(--s1);border:1px solid var(--bd);border-left:3px solid var(--${rc});border-radius:6px;cursor:pointer" tabindex="0" role="link" ${action('navigate', `/kb/${r.id}`)}><div style="display:flex;align-items:center;gap:6px;margin-bottom:2px"><span style="font-family:var(--mono);font-size:9px;padding:1px 5px;border-radius:3px;background:var(--${rc}-d);color:var(--${rc})">${esc(r.type || '')}</span><span style="font-size:12px;font-weight:500;flex:1">${esc(r.title || '')}</span></div>${rpreview ? `<div style="font-size:11px;color:var(--t3)">${esc(rpreview)}</div>` : ''}</div>`
    })
    .join('')
  return `<div class="sl">Related KB</div><div style="display:flex;flex-direction:column;gap:4px">${rows}</div>`
}

function renderKBDetail(entry: KBEntryDetail, reads: KBEntryReadStats | null): string {
  const c = kbTypeColor(entry.type)
  const tags = (entry.tags ?? [])
    .map(
      (t) =>
        `<button style="font-family:var(--mono);font-size:9px;padding:2px 6px;border-radius:3px;border:none;cursor:pointer;background:var(--s3);color:var(--t3)" ${action('goToKbWithTag', t)} aria-label="Filter Knowledge Base by tag: ${esc(t)}">${esc(t)}</button>`,
    )
    .join('')

  // Markdown rendering via the shared renderMd (marked.js + DOMPurify).
  // The previous hand-rolled line-by-line parser only handled numbered
  // lists and produced a wall-of-<p> for anything else — no headings,
  // no code blocks, no emphasis. renderMd is the canonical pipeline
  // used elsewhere in the SPA and is XSS-safe.
  const bodyHtml = renderMd(entry.body || '')

  const readsSection = reads
    ? `<div style="display:flex;align-items:center;gap:6px;margin-bottom:16px;font-family:var(--mono);font-size:10px;color:var(--t3)"><span style="color:var(--grn)">\u{1F441}</span> Read ${reads.readCount} time${reads.readCount !== 1 ? 's' : ''}${reads.recentReads.length > 0 ? ` \u00b7 last ${esc(relTime(reads.recentReads[0]!.occurredAt))}` : ''}</div>`
    : ''

  return `
    <div class="rh">
      <a class="back" tabindex="0" aria-label="Back to Knowledge Base" ${action('navigate', '/kb')}>\u2190</a>
      <div class="rh-info">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:2px">
          <span style="font-family:var(--mono);font-size:9px;padding:2px 7px;border-radius:3px;background:var(--${c}-d);color:var(--${c})">${esc(entry.type)}</span>
          <span style="font-family:var(--mono);font-size:10px;color:var(--t4)">${esc(entry.id)}</span>
          <span style="font-family:var(--mono);font-size:9px;padding:1px 6px;border-radius:99px;background:var(--s3);color:var(--t3);margin-left:auto">${esc(entry.status)}</span>
        </div>
        <h1 class="rh-t">${esc(entry.title)}</h1>
        <div style="font-family:var(--mono);font-size:10px;color:var(--t4);margin-top:3px">Created ${esc(relTime(entry.created_at))} \u00b7 Updated ${esc(relTime(entry.updated_at))}</div>
      </div>
    </div>

    <div style="display:flex;gap:4px;flex-wrap:wrap;margin-bottom:8px">${tags}</div>

    ${readsSection}

    <div class="kb-body" style="background:var(--s1);border:1px solid var(--bd);border-left:4px solid var(--${c});border-radius:var(--r2);padding:16px 20px;margin-bottom:4px">
      <div style="font-size:13px;color:var(--t2);line-height:1.75">${bodyHtml}</div>
    </div>

    ${renderLinkedWork(entry.linked_work_items)}${renderRelatedKB(entry.related_kb)}`
}
